import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { darkTheme, lightTheme } from "./theme";

type Mode = "light" | "dark";

const ThemeModeContext = createContext<{ mode: Mode; toggle: () => void }>({
  mode: "dark",
  toggle: () => {},
});

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Mode>("light");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem("yodajob-theme") as Mode | null;
    if (stored === "light" || stored === "dark") setMode(stored);
  }, []);

  const value = useMemo(
    () => ({
      mode,
      toggle: () => {
        setMode((prev) => {
          const next: Mode = prev === "dark" ? "light" : "dark";
          if (typeof window !== "undefined") window.localStorage.setItem("yodajob-theme", next);
          return next;
        });
      },
    }),
    [mode],
  );

  const theme = mode === "dark" ? darkTheme : lightTheme;

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
